import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useState, useEffect } from "react";
import Button from "../base/Button";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { format, addDays } from "date-fns";
import { useNavigate } from "react-router";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function ManageSOP() {
  const [permissions, setPermissions] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingButton, setLoadingButton] = useState(false);
  const [filterKey, setFilterKey] = useState(0);
  const navigate = useNavigate();
  const [isRevisionMode, setIsRevisionMode] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);

  const [commencementRange, setCommencementRange] = useState([null, null]);
  const [startCommencement, endCommencement] = commencementRange;
  const [reviewRange, setReviewRange] = useState([null, null]);
  const [startReview, endReview] = reviewRange;

  const [filters, setFilters] = useState({
    name: "",
    category_id: "",
    commencement_date_from: "",
    commencement_date_to: "",
    review_date_from: "",
    review_date_to: "",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });

  const handleRevise = (policy) => {
    setIsRevisionMode(true);
    setEditingPolicy(policy);

    setPolicyForm({
      name: policy.name,
      description: policy.description || "",
      category_id: policy.category_id || policy.category?.id || "",
      file_path: "",
      commencement_date: policy.current_version?.commencement_date || "",
      review_date: policy.current_version?.review_date || "",
      is_active: "1",
      is_current: "1",
    });

    setErrors({});
    setShowPolicyModal(true);
  };

  const handlePerPageChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value),
      currentPage: 1,
    }));
  };

  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  useEffect(() => {
    fetchSOPPolicies();
  }, []);
  useEffect(() => {
    if (categories.length > 0) {
      fetchSOPPolicies();
    }
  }, [pagination.currentPage, pagination.perPage, filterKey]);

  // fetch SOP and Policies
  const fetchSOPPolicies = async () => {
    setLoading(true);
    try {
      const params = {};

      if (filters.name?.trim()) params.name = filters.name.trim();
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.commencement_date_from)
        params.commencement_date_from = filters.commencement_date_from;
      if (filters.commencement_date_to)
        params.commencement_date_to = filters.commencement_date_to;
      if (filters.review_date_from)
        params.review_date_from = filters.review_date_from;
      if (filters.review_date_to)
        params.review_date_to = filters.review_date_to;

      params.page = pagination.currentPage;
      params.per_page = pagination.perPage;

      const response = await apiUrl.get("/fetch-sop-policies", { params });

      setPolicies(response?.data?.data || []);

      if (response?.data?.meta) {
        setPagination((prev) => ({
          ...prev,
          total: response.data.meta.total || 0,
          lastPage: response.data.meta.last_page || 1,
          currentPage: response.data.meta.current_page || 1,
          perPage: response.data.meta.per_page || prev.perPage,
        }));
      }
    } catch (error) {
      console.error("Error fetching SOP policies:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to fetch policies",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  // fetch categories without pagination
  const fetchCategories = async () => {
    try {
      const response = await apiUrl.get("/fetchSOPPolicyCategories");
      setCategories(response?.data?.data);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const pers = getPermissions();
    if (pers) setPermissions(pers);
  }, []);

  const [policyForm, setPolicyForm] = useState({
    name: "",
    description: "",
    category_id: "",
    file_path: "",
    commencement_date: "",
    review_date: "",
    is_active: "",
    is_current: "",
  });

  const openCreatePolicyModal = () => {
    setIsRevisionMode(false);
    setEditingPolicy(null);
    setPolicyForm({
      name: "",
      description: "",
      category_id: "",
      file_path: "",
      commencement_date: "",
      review_date: "",
      is_active: "",
      is_current: "",
    });
    setErrors({});
    setShowPolicyModal(true);
  };

  // Handle file selection (called by drag-drop or click)
  const handleFileSelect = (file) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors({
        ...errors,
        file: "Only PDF, Word, and image files are allowed",
      });
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      setErrors({ ...errors, file: "File size must be less than 25MB" });
      return;
    }

    setErrors({ ...errors, file: "" });
    setPolicyForm({ ...policyForm, file_path: file });
  };

  const handleSavePolicy = async () => {
    // ... (Keep existing validation logic exactly as is) ...
    const newErrors = {};
    if (!policyForm.category_id) newErrors.category_id = "Category is required";
    if (!policyForm.name?.trim()) newErrors.name = "Policy name is required";
    if (!policyForm.commencement_date)
      newErrors.commencement_date = "Commencement date is required";
    if (!policyForm.review_date)
      newErrors.review_date = "Review date is required";
    if (!policyForm.file_path) newErrors.file = "Document file is required";

    if (policyForm.commencement_date && policyForm.review_date) {
      if (
        new Date(policyForm.review_date) <=
        new Date(policyForm.commencement_date)
      ) {
        newErrors.review_date = "Review date must be after commencement date";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoadingButton(true);

    try {
      const formData = new FormData();
      formData.append("category_id", policyForm.category_id);
      formData.append("name", policyForm.name);
      formData.append("description", policyForm.description || "");
      formData.append("commencement_date", policyForm.commencement_date);
      formData.append("review_date", policyForm.review_date);
      formData.append("is_active", "1");
      formData.append("is_current", "1");
      formData.append("file", policyForm.file_path);

      let response;

      if (isRevisionMode && editingPolicy) {
        formData.append("policy_id", editingPolicy.id);

        response = await apiUrl.post(
          `/upload-sop-policy/${editingPolicy.id}/revise`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
      } else {
        response = await apiUrl.post("/upload-sop-policy", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      Swal.fire({
        title: response.data.message || "Success!",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      fetchSOPPolicies();
      setShowPolicyModal(false);
      setIsRevisionMode(false);
      setEditingPolicy(null);

      setPolicyForm({
        name: "",
        description: "",
        category_id: "",
        file_path: "",
        commencement_date: "",
        review_date: "",
        is_active: "",
        is_current: "",
      });
    } catch (error) {
      console.error("Upload error:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to upload policy",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoadingButton(false);
    }
  };

  const resetDateRanges = () => {
    setCommencementRange([null, null]);
    setReviewRange([null, null]);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      name: "",
      category_id: "",
      commencement_date_from: "",
      commencement_date_to: "",
      review_date_from: "",
      review_date_to: "",
    };
    resetDateRanges();
    setFilters(emptyFilters);
    setFilterKey((prev) => prev + 1);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setFilterKey((prev) => prev + 1);
  };

  const handleMakeCurrent = async (policyId, versionId) => {
    try {
      const response = await apiUrl.post("/policies/make-current", {
        policy_id: policyId,
        version_id: versionId,
      });

      if (response.data?.status) {
        fetchSOPPolicies();
      }
    } catch (error) {
      console.error("Make current error:", error);
    }
  };

  return (
    <>
      {permissions?.upload_sop_and_policies && (
        <div className="card px-4 py-3 bg-white ">
          <header className="mb-5">
            <div className="flex items-center justify-between flex-wrap">
              <div>
                <h1 className="h3 fw-bold">SOP & Policies Categories</h1>
                <p className="text-muted small">Mangage SOP & Policies here</p>
              </div>
              <div>
                <Button
                  text="Upload SOP, Policy"
                  size="md"
                  variant="primary"
                  onClick={openCreatePolicyModal}
                />
              </div>
            </div>
          </header>

          {/* Filters Section - Improved UI */}
          <div className="mb-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
            <div className="row g-3">
              {/* Policy Name Input */}
              <div className="col-md-3 col-12">
                <label className="form-label small fw-medium mb-1">
                  Policy Name
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Policy name or ID"
                  value={filters.name}
                  onChange={(e) =>
                    setFilters({ ...filters, name: e.target.value })
                  }
                />
              </div>

              {/* Category Dropdown */}
              <div className="col-md-3 col-12">
                <label className="form-label small fw-medium mb-1">
                  Category
                </label>
                <select
                  className="form-select form-select-sm"
                  value={filters.category_id}
                  onChange={(e) =>
                    setFilters({ ...filters, category_id: e.target.value })
                  }
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Commencement Date Range */}
              <div className="col-md-3 col-12">
                <label className="form-label small fw-medium mb-1">
                  Commencement Date
                </label>
                <DatePicker
                  selectsRange
                  startDate={startCommencement}
                  endDate={endCommencement}
                  onChange={(update) => {
                    setCommencementRange(update);
                    setFilters({
                      ...filters,
                      commencement_date_from: update[0]
                        ? format(update[0], "yyyy-MM-dd")
                        : "",
                      commencement_date_to: update[1]
                        ? format(update[1], "yyyy-MM-dd")
                        : "",
                    });
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  wrapperClassName="w-100"
                />
              </div>

              {/* Review Date Range */}
              <div className="col-md-3 col-12">
                <label className="form-label small fw-medium mb-1">
                  Review Date Range
                </label>
                <DatePicker
                  selectsRange
                  startDate={startReview}
                  endDate={endReview}
                  onChange={(update) => {
                    setReviewRange(update);
                    setFilters({
                      ...filters,
                      review_date_from: update[0]
                        ? format(update[0], "yyyy-MM-dd")
                        : "",
                      review_date_to: update[1]
                        ? format(update[1], "yyyy-MM-dd")
                        : "",
                    });
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                  wrapperClassName="w-100"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="d-flex justify-content-end gap-2 mt-3 pt-3 ">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleClearFilters}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear All
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleApplyFilters}
              >
                <i className="bi bi-funnel me-1"></i>
                Apply Filters
              </button>
            </div>
          </div>

          <div className="row g-4">
            {loading ? (
              <>
                {[...Array(6)].map((_, i) => (
                  <div className="col-md-6 col-lg-6" key={`skeleton-${i}`}>
                    <div className="card border-0 shadow-sm rounded-4 animate-pulse">
                      <div className="card-body p-4">
                        <div className="h-5 w-3/4 bg-gray-200 rounded mb-3"></div>
                        <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 w-2/3 bg-gray-200 rounded mb-4"></div>
                        <div className="space-y-2 mb-4">
                          <div className="h-8 w-full bg-gray-200 rounded"></div>
                          <div className="h-8 w-full bg-gray-200 rounded"></div>
                        </div>
                        <div className="d-flex justify-content-end gap-2">
                          <div className="h-8 w-16 bg-gray-200 rounded"></div>
                          <div className="h-8 w-16 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : policies?.length === 0 ? (
              <div className="col-12">
                <div className="text-center py-10">
                  <div className="text-muted">
                    <svg
                      className="mx-auto mb-3 opacity-50"
                      width="48"
                      height="48"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="fw-medium mb-1">No policies found</p>
                    <p className="small mb-3">
                      Try adjusting your filters or upload a new policy.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              policies?.map((policy) => {
                const versions = policy.versions || [];

                return (
                  <div
                    className="col-md-6 col-lg-6 border-black shadow-sm"
                    key={policy?.id}
                  >
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-4 pb-2">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5
                              className="card-title fw-bold text-primary mb-1"
                              title={policy?.name}
                            >
                              {policy?.name || "Untitled Policy"}
                            </h5>
                            <span className="badge bg-light text-dark border">
                              {policy?.category?.name || "Uncategorized"}
                            </span>
                          </div>

                          {!!permissions?.review_sop_and_policies && (
                            <button
                              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                              onClick={() => handleRevise(policy)}
                              title="Create Revised Version"
                            >
                              <i className="bi bi-pencil-square"></i> Revise
                            </button>
                          )}
                        </div>

                        <p
                          className="card-text text-muted small mb-3 whitespace-normal break-words"
                          title={policy?.description}
                        >
                          {policy?.description || (
                            <span className="fst-italic">No description</span>
                          )}
                        </p>
                      </div>

                      <hr className="mx-4 my-0 text-muted opacity-25" />

                      <div className="px-4 py-3">
                        <small className="text-muted fw-medium d-block mb-2">
                          Version History ({versions.length})
                        </small>

                        <div
                          className="d-flex flex-column gap-2"
                          style={{ maxHeight: "180px", overflowY: "auto" }}
                        >
                          {versions.map((version) => (
                            <div
                              key={version.id}
                              className="d-flex align-items-center justify-content-between p-2 rounded bg-light"
                            >
                              <div className="d-flex gap-3 mb-3 small">
                                <div>
                                  <span className="text-muted">Commence:</span>
                                  <div className="fw-medium">
                                    {version?.commencement_date
                                      ? new Date(
                                          version.commencement_date,
                                        ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })
                                      : "-"}
                                  </div>
                                </div>

                                <div>
                                  <span className="text-muted">Review:</span>
                                  <div className="fw-medium">
                                    {version?.review_date
                                      ? new Date(
                                          version.review_date,
                                        ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })
                                      : "Not set"}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  {version.is_current === 1 ? (
                                    <span className="badge bg-success">
                                      Current
                                    </span>
                                  ) : (
                                    <span
                                      className="text-primary small cursor-pointer hover:underline"
                                      onClick={() =>
                                        handleMakeCurrent(policy.id, version.id)
                                      }
                                      title="Set this version as current"
                                      style={{ cursor: "pointer" }}
                                    >
                                      Make Current
                                    </span>
                                  )}
                                </div>
                              </div>

                              <a
                                href={version.file_url || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary py-0 px-3 flex-shrink-0"
                                title="View Document"
                                style={{
                                  fontSize: "0.75rem",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <i className="bi bi-file-earmark-pdf me-1"></i>
                                View
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* pagination */}
          <div className="row align-items-center mb-5 mt-2">
            <div className="col-auto">
              <select
                value={pagination?.perPage}
                onChange={handlePerPageChange}
                className="form-select"
                style={{ minWidth: 80 }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
            <div className="col-auto text-muted">
              Page {pagination.currentPage} – Showing{" "}
              {Math.min(
                pagination.perPage,
                pagination.total -
                  (pagination.currentPage - 1) * pagination.perPage,
              )}{" "}
              of {pagination.total} entries
            </div>
            <div className="col">
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                breakLabel={"..."}
                pageCount={pagination.lastPage}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageChange}
                forcePage={pagination.currentPage - 1}
                containerClassName={"pagination justify-content-end mb-0"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
                disabledClassName={"disabled"}
              />
            </div>
          </div>
          {/* show policy upload modal */}
          {showPolicyModal && (
            <>
              <div
                className="modal-backdrop fade show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(0,0,0,0.45)",
                  zIndex: 1040,
                }}
                onClick={() => setShowPolicyModal(false)}
              />

              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                style={{ zIndex: 1050 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content border-0 rounded-4 shadow">
                    {/* <div
                      className="modal-header px-4 py-3"
                      style={{
                        background: "#f7f8fa",
                        borderBottom: "1px solid #eef2f6",
                      }}
                    >
                      <div>
                        <h5 className="modal-title fw-bold h6">
                          Upload SOP & Policies Category
                        </h5>
                        <p className="text-muted small mb-0 mt-1">
                          Fill in the details and upload your document
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowPolicyModal(false)}
                      />
                    </div> */}
                    {/* Modal Header */}
                    <div
                      className="modal-header px-4 py-3"
                      style={{
                        background: "#f7f8fa",
                        borderBottom: "1px solid #eef2f6",
                      }}
                    >
                      <div>
                        <h5 className="modal-title fw-bold h6">
                          {isRevisionMode
                            ? "Revise Policy Document"
                            : "Upload SOP & Policies Category"}
                        </h5>
                        <p className="text-muted small mb-0 mt-1">
                          {isRevisionMode
                            ? "Upload a new version for existing policy"
                            : "Fill in the details and upload your document"}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => {
                          setShowPolicyModal(false);
                          setIsRevisionMode(false);
                          setEditingPolicy(null);
                        }}
                      />
                    </div>

                    {/* body */}
                    <div className="modal-body px-4 py-3">
                      <div className="row g-3 mb-3">
                        {/* Category Selection */}
                        <div className="col-md-6">
                          <label className="form-label small fw-medium mb-1">
                            Category <span className="text-danger">*</span>
                          </label>
                          <select
                            disabled={isRevisionMode}
                            className={`form-select ${errors.category_id ? "is-invalid" : ""}`}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                            }}
                            value={policyForm.category_id}
                            onChange={(e) => {
                              setPolicyForm({
                                ...policyForm,
                                category_id: e.target.value,
                              });
                              if (errors.category_id)
                                setErrors({ ...errors, category_id: "" });
                            }}
                          >
                            <option value="">Select a category</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                          {errors.category_id && (
                            <div
                              className="invalid-feedback d-block"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {errors.category_id}
                            </div>
                          )}
                        </div>

                        {/* Policy Name */}
                        <div className="col-md-6">
                          <label className="form-label small fw-medium mb-1">
                            Policy Name <span className="text-danger">*</span>
                          </label>
                          <input
                            disabled={isRevisionMode}
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                            }}
                            placeholder="e.g., Remote Work Policy"
                            value={policyForm.name}
                            onChange={(e) => {
                              setPolicyForm({
                                ...policyForm,
                                name: e.target.value,
                              });
                              if (errors.name)
                                setErrors({ ...errors, name: "" });
                            }}
                          />
                          {errors.name && (
                            <div
                              className="invalid-feedback d-block"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {errors.name}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-3">
                        <label className="form-label small fw-medium mb-1">
                          Description{" "}
                          <span className="text-muted fw-normal">
                            (optional)
                          </span>
                        </label>
                        <textarea
                          disabled={isRevisionMode}
                          className="form-control"
                          style={{
                            padding: "0.375rem 0.75rem",
                            fontSize: "0.875rem",
                            resize: "none",
                          }}
                          rows={3}
                          placeholder="Brief description of what this policy covers..."
                          value={policyForm.description}
                          onChange={(e) =>
                            setPolicyForm({
                              ...policyForm,
                              description: e.target.value,
                            })
                          }
                        />
                      </div>

                      {/* Dates Grid */}
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label small fw-medium mb-1">
                            Commencement Date Range{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            className={`form-control ${errors.commencement_date ? "is-invalid" : ""}`}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                            }}
                            value={policyForm.commencement_date}
                            onChange={(e) => {
                              setPolicyForm({
                                ...policyForm,
                                commencement_date: e.target.value,
                              });
                              if (errors.commencement_date)
                                setErrors({ ...errors, commencement_date: "" });
                            }}
                          />
                          {errors.commencement_date && (
                            <div
                              className="invalid-feedback d-block"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {errors.commencement_date}
                            </div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label small fw-medium mb-1">
                            Review Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            className={`form-control ${errors.review_date ? "is-invalid" : ""}`}
                            style={{
                              padding: "0.375rem 0.75rem",
                              fontSize: "0.875rem",
                            }}
                            value={policyForm.review_date}
                            onChange={(e) => {
                              setPolicyForm({
                                ...policyForm,
                                review_date: e.target.value,
                              });
                              if (errors.review_date)
                                setErrors({ ...errors, review_date: "" });
                            }}
                          />
                          {errors.review_date && (
                            <div
                              className="invalid-feedback d-block"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {errors.review_date}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 📁 File Upload */}
                      <div className="mb-0">
                        <label className="form-label small fw-medium mb-1">
                          Upload Document <span className="text-danger">*</span>
                        </label>

                        <div
                          className={`border border-dashed rounded-3 p-4 text-center cursor-pointer transition-all ${
                            errors.file
                              ? "border-danger bg-danger-subtle"
                              : policyForm.file_path
                                ? "border-success bg-success-subtle"
                                : "border-secondary-subtle bg-light hover:border-primary"
                          }`}
                          style={{ minHeight: "100px" }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add(
                              "border-primary",
                              "bg-primary-subtle",
                            );
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove(
                              "border-primary",
                              "bg-primary-subtle",
                            );
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove(
                              "border-primary",
                              "bg-primary-subtle",
                            );
                            const file = e.dataTransfer.files[0];
                            if (file) handleFileSelect(file);
                          }}
                          onClick={() =>
                            document
                              .getElementById("file-upload-input")
                              ?.click()
                          }
                        >
                          <input
                            id="file-upload-input"
                            type="file"
                            className="d-none"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif"
                            onChange={(e) =>
                              e.target.files[0] &&
                              handleFileSelect(e.target.files[0])
                            }
                          />

                          {!policyForm.file_path ? (
                            <div>
                              <div className="mb-2">
                                <svg
                                  className="mx-auto text-primary"
                                  width="28"
                                  height="28"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                  />
                                </svg>
                              </div>
                              <p className="small mb-0">
                                <span className="text-primary fw-medium">
                                  Click to upload
                                </span>{" "}
                                or drag & drop
                              </p>
                              <p className="text-muted small mb-0 mt-1">
                                PDF, Word, PNG, JPG (Max 25MB)
                              </p>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center justify-content-between bg-white rounded-2 p-2 border">
                              <div className="d-flex align-items-center gap-2 text-start">
                                <div className="bg-success-subtle rounded-1 p-1">
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                </div>
                                <div className="min-w-0">
                                  <p
                                    className="small fw-medium mb-0 text-truncate"
                                    style={{ maxWidth: "180px" }}
                                  >
                                    {policyForm.file_path?.name ||
                                      "Document selected"}
                                  </p>
                                  <p
                                    className="text-muted small mb-0"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {policyForm.file_path?.size
                                      ? `${(policyForm.file_path.size / 1024 / 1024).toFixed(2)} MB`
                                      : ""}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm text-muted p-0 border-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPolicyForm({
                                    ...policyForm,
                                    file_path: null,
                                  });
                                  if (errors.file)
                                    setErrors({ ...errors, file: "" });
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>

                        {/* ✅ File error shows below */}
                        {errors.file && (
                          <div
                            className="invalid-feedback d-block mt-2"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {errors.file}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="modal-footer px-4 py-3"
                      style={{
                        background: "#f7f8fa",
                        borderTop: "1px solid #eef2f6",
                      }}
                    >
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowPolicyModal(false)}
                      >
                        Close
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleSavePolicy}
                        disabled={loadingButton}
                      >
                        {loadingButton ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ManageSOP;
